import {
  AptunoButton,
  FileUploader2,
  FileUploaderButton,
  HeaderSimple,
  Icon,
  Modal,
  SIZES,
  useResponsive,
  useToast,
  VARIANTS,
} from '@aptuno/aptuno-ui';
import { DocumentStatus, IdentityValidationDocumentName } from '@contexts/IdentityValidation/domain';
import { IdentityValidationCompleted } from '@contexts/IdentityValidation/domain/events/identity-validation-completed.event';
import { IdentityValidationStarted } from '@contexts/IdentityValidation/domain/events/identity-validation-started.event';
import { useAnalyticsTracker } from '@services/analytics';
import { useLoggedInUser } from '@shared/Authentication';
import { AptunoError, GeneralAptunoErrorType, Response } from '@shared/models';
import { compressFile, getFileFromSesssionStorage, saveFileInSessionStorage } from '@shared/utils';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useIdentityValidationActions } from '../../store';
import { useDocumentActions } from '../../store/documents.actions.context';
import { LoadingStatus, useUploadFile } from '../../utils';
import { LoadPhotosInfoScreen } from '../LoadPhotosInfoScreen/LoadPhotosInfoScreen';
import {
  ActionBarRightButton,
  ActionsBarRoot,
  HeaderSection,
  HorizontalSubHeader,
  IconClose,
  InitIdentityValidationScreenContent,
  LoadDocumentsBox,
  NormalTextContainer,
  PhotoInfoButton,
  PhotoInfoContainer,
  PhotoInfoSubtitle,
  PhotoInfoSubtitleBlack,
  SubtitleWeightVariant,
  TextNormal,
  Title,
  TitleText,
  InitIdentityValidationScreenRoot,
  LoadPhotosContainer,
  UploadPictureButtonContainer,
  FileTypeErrorModalFooterRoot,
  FileTypeErrorModalHeader,
  FileTypeErrorModalTitle,
} from './InitIdentityValidationScreen.styled';

const PICTURE_VALID_FILE_TYPES = ['image/jpg', 'image/jpeg', 'image/png'];
const DOCUMENT_VALID_FILE_TYPES = ['image/jpg', 'image/jpeg', 'image/png'];
const MAX_DNI_PHOTOS = 2;
const SESSION_STORAGE_DNI_FIRST_PHOTO_KEY = 'identity-validation-dni-first-photo';
const SESSION_STORAGE_DNI_SECOND_PHOTO_KEY = 'identity-validation-dni-second-photo';
const SESSION_STORAGE_PICTURE_KEY = 'identity-validation-picture';
const LOAD_IMAGE_ERROR_MESSAGE_LIFETIME = 3000;

type Props = {
  onFinish: () => void;
  onLeave: () => void;
};

export const InitIdentityValidationScreen = ({ onFinish, onLeave }: Props) => {
  const toast = useToast();
  const { isMobile } = useResponsive();
  const eventTracker = useAnalyticsTracker();
  const user = useLoggedInUser();

  const router = useRouter();

  const { userDocument, getUserDocument } =
    useIdentityValidationActions().identityValidationQueryActions.useGetUserDocument();
  const {
    initializeIdentityValidation,
    loading: initializingIdentityValidation,
    initializedIdentityValidation,
  } = useIdentityValidationActions().identityValidationCommandActions.useInitializeIdentityValidation();
  const {
    result: userIdentityValidation,
    get: getUserIdentityValidation,
    recall: reloadUserIdentityValidation,
  } = useIdentityValidationActions().identityValidationQueryActions.useGetIdentityValidation();

  const {
    uploadFile: uploadPicture,
    uploadedFile: uploadedPicture,
    uploadingStatus: uploadingPictureStatus,
  } = useUploadFile();
  const {
    uploadFile: uploadFirstDniPhoto,
    uploadedFile: uploadedFirstDniPhoto,
    uploadingStatus: uploadingFirstDniPhotoStatus,
  } = useUploadFile();
  const {
    uploadFile: uploadSecondDniPhoto,
    uploadedFile: uploadedSecondDniPhoto,
    uploadingStatus: uploadingSecondDniPhotoStatus,
  } = useUploadFile();

  const { deleteDocument: deletePicture, documentDeleted: pictureDeleted } =
    useDocumentActions().documentsCommandActions.useDeleteDocument();
  const { deleteDocument: deleteFirstDni, documentDeleted: firstDniDeleted } =
    useDocumentActions().documentsCommandActions.useDeleteDocument();
  const { deleteDocument: deleteSecondDni, documentDeleted: secondDniDeleted } =
    useDocumentActions().documentsCommandActions.useDeleteDocument();

  const [localPicture, setLocalPicture] = useState<File[]>([]);
  const [localDniPhotos, setLocalDniPhotos] = useState<File[]>([]);
  const [showPictureInfo, setShowPictureInfo] = useState<boolean>(false);
  const [showTypeFileErrorModal, setShowTypeFileErrorModal] = useState<boolean>(false);
  const [isIdentityValidationEditable, setIsIdentityValidationEditable] = useState<boolean>(true);

  useEffect(() => {
    if (router.query && router.query.estateLegalId) {
      router.push({
        pathname: `/estate-application/${router.query.estateLegalId}`,
        query: { ...router.query, stage: 'identity-validation' },
      });
    }
  }, [router.query.estateLegalId]);

  useEffect(() => {
    getUserDocument();
    getUserIdentityValidation();
  }, []);

  useEffect(() => {
    if (!!pictureDeleted) {
      reloadUserIdentityValidation().then(() => {
        sessionStorage.removeItem(SESSION_STORAGE_PICTURE_KEY);
        setLocalPicture(() => []);
      });
    }
  }, [pictureDeleted]);

  useEffect(() => {
    if (!!firstDniDeleted) {
      reloadUserIdentityValidation().then(() => {
        sessionStorage.removeItem(SESSION_STORAGE_DNI_FIRST_PHOTO_KEY);
        setLocalDniPhotos((current) =>
          current.filter((file) => file.name != IdentityValidationDocumentName.IDENTITY_DOCUMENT)
        );
      });
    }
  }, [firstDniDeleted]);

  useEffect(() => {
    if (!!secondDniDeleted) {
      reloadUserIdentityValidation().then(() => {
        sessionStorage.removeItem(SESSION_STORAGE_DNI_SECOND_PHOTO_KEY);
        setLocalDniPhotos((current) =>
          current.filter((file) => file.name != IdentityValidationDocumentName.IDENTITY_DOCUMENT_2)
        );
      });
    }
  }, [secondDniDeleted]);

  useEffect(() => {
    let isFetchingPicture = true;

    if (Response.isSucceed(userIdentityValidation)) {
      const isPictureCurrentlyLoaded = userIdentityValidation.result.documents.picture.status == DocumentStatus.LOADED;
      const isPictureLocallySaved = localPicture.length > 0;

      if (isPictureCurrentlyLoaded && !isPictureLocallySaved) {
        getFileFromSesssionStorage(SESSION_STORAGE_PICTURE_KEY, IdentityValidationDocumentName.PICTURE).then(
          (picture: File | undefined) => {
            const currentPicture =
              picture || new File([], IdentityValidationDocumentName.PICTURE, { type: 'image/pdf' });

            isFetchingPicture && setLocalPicture(() => [currentPicture]);
          }
        );
      }
    }

    return () => {
      isFetchingPicture = false;
    };
  }, [userIdentityValidation.type, localPicture]);

  useEffect(() => {
    let isFetchingFirstDni = true;

    if (Response.isSucceed(userIdentityValidation)) {
      const isFirstDniCurrentlyLoaded =
        userIdentityValidation.result.documents.document1.status == DocumentStatus.LOADED;
      const isFirstDniLocallySaved = localDniPhotos.some(
        (dniDocument) => dniDocument.name == IdentityValidationDocumentName.IDENTITY_DOCUMENT
      );

      if (isFirstDniCurrentlyLoaded && !isFirstDniLocallySaved) {
        getFileFromSesssionStorage(
          SESSION_STORAGE_DNI_FIRST_PHOTO_KEY,
          IdentityValidationDocumentName.IDENTITY_DOCUMENT
        ).then((dniPhoto: File | undefined) => {
          const currentDniPhoto =
            dniPhoto || new File([], IdentityValidationDocumentName.IDENTITY_DOCUMENT, { type: 'image/pdf' });

          isFetchingFirstDni && setLocalDniPhotos((prev) => [...prev, currentDniPhoto]);
        });
      }
    }

    return () => {
      isFetchingFirstDni = false;
    };
  }, [userIdentityValidation.type, localDniPhotos]);

  useEffect(() => {
    let isFetchingSecondDni = true;

    if (Response.isSucceed(userIdentityValidation)) {
      const isSecondDniCurrentlyLoaded =
        userIdentityValidation.result.documents.document2.status == DocumentStatus.LOADED;
      const isSecondDniLocallySaved = localDniPhotos.some(
        (dniDocument) => dniDocument.name == IdentityValidationDocumentName.IDENTITY_DOCUMENT_2
      );

      if (isSecondDniCurrentlyLoaded && !isSecondDniLocallySaved) {
        getFileFromSesssionStorage(
          SESSION_STORAGE_DNI_SECOND_PHOTO_KEY,
          IdentityValidationDocumentName.IDENTITY_DOCUMENT_2
        ).then((dniPhoto: File | undefined) => {
          const currentDniPhoto =
            dniPhoto || new File([], IdentityValidationDocumentName.IDENTITY_DOCUMENT_2, { type: 'image/pdf' });

          isFetchingSecondDni && setLocalDniPhotos((prev) => [...prev, currentDniPhoto]);
        });
      }
    }

    return () => {
      isFetchingSecondDni = false;
    };
  }, [userIdentityValidation.type, localDniPhotos]);

  useEffect(() => {
    if (!!uploadedPicture && LoadingStatus.LOADED == uploadingPictureStatus) {
      setLocalPicture(() => [uploadedPicture]);

      reloadUserIdentityValidation().then(() => {
        setShowPictureInfo(() => false);
        saveFileInSessionStorage(uploadedPicture, SESSION_STORAGE_PICTURE_KEY);
      });
    }
  }, [uploadedPicture, uploadingPictureStatus]);

  useEffect(() => {
    if (!!uploadedFirstDniPhoto && LoadingStatus.LOADED == uploadingFirstDniPhotoStatus) {
      const uploadedFile = new File(
        [uploadedFirstDniPhoto.slice(undefined, uploadedFirstDniPhoto.size, uploadedFirstDniPhoto.type)],
        IdentityValidationDocumentName.IDENTITY_DOCUMENT,
        { type: uploadedFirstDniPhoto.type }
      );

      setLocalDniPhotos((current) => [
        ...current.filter((file) => IdentityValidationDocumentName.IDENTITY_DOCUMENT != file.name),
        uploadedFile,
      ]);

      reloadUserIdentityValidation().then(() => {
        saveFileInSessionStorage(uploadedFirstDniPhoto, SESSION_STORAGE_DNI_FIRST_PHOTO_KEY);
      });
    }
  }, [uploadedFirstDniPhoto, uploadingFirstDniPhotoStatus]);

  useEffect(() => {
    if (!!uploadedSecondDniPhoto && LoadingStatus.LOADED == uploadingSecondDniPhotoStatus) {
      const uploadedFile = new File(
        [uploadedSecondDniPhoto.slice(undefined, uploadedSecondDniPhoto.size, uploadedSecondDniPhoto.type)],
        IdentityValidationDocumentName.IDENTITY_DOCUMENT_2,
        { type: uploadedSecondDniPhoto.type }
      );

      setLocalDniPhotos((current) => [
        ...current.filter((file) => IdentityValidationDocumentName.IDENTITY_DOCUMENT_2 !== file.name),
        uploadedFile,
      ]);

      reloadUserIdentityValidation().then(() => {
        saveFileInSessionStorage(uploadedSecondDniPhoto, SESSION_STORAGE_DNI_SECOND_PHOTO_KEY);
      });
    }
  }, [uploadedSecondDniPhoto, uploadingSecondDniPhotoStatus]);

  useEffect(() => {
    if (Response.isSucceed(userIdentityValidation) && userIdentityValidation.result.notLongerEditable()) {
      setIsIdentityValidationEditable(() => false);
    }
  }, [userIdentityValidation]);

  useEffect(() => {
    if (
      userDocument?.identityDocumentCode &&
      userDocument?.identityDocumentType &&
      !initializingIdentityValidation &&
      !initializedIdentityValidation &&
      Response.isError(userIdentityValidation) &&
      GeneralAptunoErrorType.isResourceNotFound(userIdentityValidation.error as AptunoError)
    ) {
      initializeIdentityValidation();
    }
  }, [userDocument, userIdentityValidation]);

  useEffect(() => {
    if (!isIdentityValidationEditable) {
      onFinish();
    }
  }, [isIdentityValidationEditable]);

  useEffect(() => {
    if (initializedIdentityValidation) {
      user?.data?.id && eventTracker.trackEvent(new IdentityValidationStarted(user.data.id));
    }
  }, [initializedIdentityValidation]);

  useEffect(() => {
    if (!!initializedIdentityValidation) {
      reloadUserIdentityValidation();
    }
  }, [initializedIdentityValidation]);

  const handleAddPicture = (file: File) => {
    if (file && localPicture.length == 0)
      if (Response.isSucceed(userIdentityValidation) && PICTURE_VALID_FILE_TYPES.includes(file.type)) {
        compressFile(
          file,
          (compressedImage: File) => {
            uploadPicture({
              file: compressedImage,
              name: IdentityValidationDocumentName.PICTURE,
              identityValidationId: userIdentityValidation.result.id.value,
            });
          },
          (error: Error) => handleCompressImageError(error, file.name)
        );
      } else handleInvalidPictureLoaded();
  };

  const handleAddDniPhoto = (file: File) => {
    const isFirstDniAlreadyUploaded = localDniPhotos.some(
      (found) => found.name === IdentityValidationDocumentName.IDENTITY_DOCUMENT
    );

    const isSecondDniAlreadyUploaded = localDniPhotos.some(
      (found) => found.name === IdentityValidationDocumentName.IDENTITY_DOCUMENT_2
    );

    if (Response.isSucceed(userIdentityValidation) && DOCUMENT_VALID_FILE_TYPES.includes(file.type))
      compressFile(
        file,
        (compressedImage: File) => {
          if (!isFirstDniAlreadyUploaded) {
            uploadFirstDniPhoto({
              file: compressedImage,
              name: IdentityValidationDocumentName.IDENTITY_DOCUMENT,
              identityValidationId: userIdentityValidation.result.id.value,
            });
          } else if (!isSecondDniAlreadyUploaded) {
            uploadSecondDniPhoto({
              file: compressedImage,
              name: IdentityValidationDocumentName.IDENTITY_DOCUMENT_2,
              identityValidationId: userIdentityValidation.result.id.value,
            });
          }
        },
        (error: Error) => handleCompressImageError(error, file.name)
      );
  };

  const handleRemovePicture = () => {
    if (Response.isSucceed(userIdentityValidation)) {
      deletePicture({
        identityValidationId: userIdentityValidation.result.id.value,
        documentToDelete: IdentityValidationDocumentName.PICTURE,
      });
    }
  };

  const handleRemoveDniPhoto = (remaining: File[], removedFile: File) => {
    if (Response.isSucceed(userIdentityValidation)) {
      if (removedFile.name === IdentityValidationDocumentName.IDENTITY_DOCUMENT)
        deleteFirstDni({
          identityValidationId: userIdentityValidation.result.id.value,
          documentToDelete: IdentityValidationDocumentName.IDENTITY_DOCUMENT,
        });
      else
        deleteSecondDni({
          identityValidationId: userIdentityValidation.result.id.value,
          documentToDelete: IdentityValidationDocumentName.IDENTITY_DOCUMENT_2,
        });
    }
  };

  const handleCompressImageError = (_: Error, imageName: string) =>
    toast.pushWarning(
      `Ha ocurrido un error al cargar la imagen ${imageName}. Por favor vuelva a intentar.`,
      'Error al cargar imagen',
      LOAD_IMAGE_ERROR_MESSAGE_LIFETIME
    );

  const handlePictureInfo = () => {
    setShowPictureInfo(() => true);
  };

  const isSelfieInfoDisabled = () => false;

  const handleFinish = async () =>
    user?.data?.id && eventTracker.trackEvent(new IdentityValidationCompleted(user.data.id)).finally(() => onFinish());

  const handleClosePictureInfoScreen = () => setShowPictureInfo(() => false);

  const handleInvalidPictureLoaded = () => setShowTypeFileErrorModal(() => true);

  const handleInvalidDniPhotoLoaded = () => setShowTypeFileErrorModal(() => true);

  const handleCloseTypeFileErrorModal = () => setShowTypeFileErrorModal(() => false);

  const getLoadingDniPhotos = (): File[] => {
    const firstDniPhoto =
      uploadingFirstDniPhotoStatus === LoadingStatus.LOADING
        ? [new File([], IdentityValidationDocumentName.IDENTITY_DOCUMENT)]
        : [];
    const secondDniPhoto =
      uploadingSecondDniPhotoStatus === LoadingStatus.LOADING
        ? [new File([], IdentityValidationDocumentName.IDENTITY_DOCUMENT_2)]
        : [];

    return [...firstDniPhoto, ...secondDniPhoto];
  };

  const getDniPhotos = (): File[] => [...localDniPhotos, ...getLoadingDniPhotos()];

  const getLoadingPicture = (): File[] =>
    uploadingPictureStatus === LoadingStatus.LOADING ? [new File([], IdentityValidationDocumentName.PICTURE)] : [];

  const getPicture = (): File[] => [...localPicture, ...getLoadingPicture()];

  return (
    <InitIdentityValidationScreenRoot>
      {showPictureInfo ? (
        <LoadPhotosInfoScreen
          onBackClick={handleClosePictureInfoScreen}
          onLeave={handleClosePictureInfoScreen}
          onAddPicture={handleAddPicture}
          isLoadPictureDisabled={localPicture.length > 0}
        />
      ) : (
        <>
          <HeaderSimple withBorderRadius={false} maxWidth="100%" isFixed={false}>
            <TitleText>Verificación de identidad</TitleText>
            <IconClose icon={'closeAlt'} color={'#9B9B9B'} marginRight={'16px !important'} onClick={onLeave} />
          </HeaderSimple>
          <InitIdentityValidationScreenContent isMobile={isMobile}>
            <HeaderSection horizontalAlign="center" verticalAlign="center" padding={'0'}>
              <Title isMobile={isMobile}>No dudamos que seas tú, pero queremos estar seguros.</Title>
              <HorizontalSubHeader>
                <img src={`/images/id_verification.svg`} alt="Application in progress" />
                <PhotoInfoContainer>
                  <TextNormal align="start">
                    Tómate una foto sosteniendo tu documento de identidad cerca a tu cara.
                  </TextNormal>
                  {!isMobile && (
                    <PhotoInfoButton
                      isMobile={isMobile}
                      disabled={isSelfieInfoDisabled()}
                      appearance={VARIANTS.SECONDARY_LIGHT}
                      size={SIZES.EXTRA_SMALL}
                      onClick={handlePictureInfo}
                    >
                      <Icon icon={'question'} />
                      ¿Cómo me tomo la foto?
                    </PhotoInfoButton>
                  )}
                </PhotoInfoContainer>
              </HorizontalSubHeader>
              {isMobile && (
                <PhotoInfoButton
                  isMobile={isMobile}
                  disabled={isSelfieInfoDisabled()}
                  appearance={VARIANTS.SECONDARY_LIGHT}
                  size={SIZES.EXTRA_SMALL}
                  onClick={handlePictureInfo}
                >
                  <Icon icon={'question'} />
                  ¿Cómo me tomo la foto?
                </PhotoInfoButton>
              )}
              <LoadPhotosContainer>
                {localPicture.length == 0 && getLoadingPicture().length == 0 ? (
                  <UploadPictureButtonContainer>
                    <FileUploaderButton
                      onFileAdded={handleAddPicture}
                      accept={PICTURE_VALID_FILE_TYPES}
                      appearance={VARIANTS.SECONDARY_LIGHT}
                      fontColor={'black'}
                    >
                      <Icon icon={'plus'} />
                      Cargar foto
                    </FileUploaderButton>
                  </UploadPictureButtonContainer>
                ) : (
                  <>
                    <FileUploader2
                      withErrors={[]}
                      disabled={localPicture.length > 0}
                      buttonText="Cargar foto"
                      buttonVariant={VARIANTS.SECONDARY_LIGHT}
                      accept={PICTURE_VALID_FILE_TYPES}
                      onAddFile={handleAddPicture}
                      imagePreview
                      files={getPicture()}
                      previewProps={{ align: 'start' }}
                      backgroundColor="transparent"
                      onRemoveFile={handleRemovePicture}
                      minHeight={'188px'}
                      onInvalidInput={handleInvalidPictureLoaded}
                      loadingFiles={getLoadingPicture().map((file) => file.name)}
                      allowToRemove={isIdentityValidationEditable}
                    />
                  </>
                )}
              </LoadPhotosContainer>
              <NormalTextContainer>
                <PhotoInfoSubtitle align="start" weight={SubtitleWeightVariant.BOLDER}>
                  <PhotoInfoSubtitleBlack>Sube aquí fotos de tu documento de identidad</PhotoInfoSubtitleBlack> por
                  ambos lados
                </PhotoInfoSubtitle>
                <PhotoInfoSubtitle>Sube tus documentos en formato de imagen.</PhotoInfoSubtitle>
              </NormalTextContainer>
              <LoadDocumentsBox expandLoadDocumentButton={localDniPhotos.length > 0}>
                <FileUploader2
                  withErrors={[]}
                  disabled={localDniPhotos.length >= MAX_DNI_PHOTOS}
                  buttonText="Cargar documentos"
                  buttonVariant={VARIANTS.SECONDARY_LIGHT}
                  accept={DOCUMENT_VALID_FILE_TYPES}
                  onAddFile={handleAddDniPhoto}
                  imagePreview
                  files={getDniPhotos()}
                  previewProps={{
                    align: 'start',
                  }}
                  backgroundColor="transparent"
                  onRemoveFile={handleRemoveDniPhoto}
                  minHeight={'188px'}
                  onInvalidInput={handleInvalidDniPhotoLoaded}
                  loadingFiles={getLoadingDniPhotos().map((file) => file.name)}
                  allowToRemove={isIdentityValidationEditable}
                />
              </LoadDocumentsBox>
            </HeaderSection>
            <ActionsBarRoot>
              <ActionBarRightButton
                disabled={isIdentityValidationEditable}
                appearance={VARIANTS.PRIMARY}
                size={SIZES.BIGGEST}
                onClick={handleFinish}
                isMobile={isMobile}
              >
                Continuar
                <Icon width={'12px'} height={'12px'} icon="Arrow_Right" />
              </ActionBarRightButton>
            </ActionsBarRoot>
          </InitIdentityValidationScreenContent>
        </>
      )}
      <Modal
        size={600}
        isActive={showTypeFileErrorModal}
        onClose={handleCloseTypeFileErrorModal}
        header={
          <FileTypeErrorModalHeader>
            <FileTypeErrorModalTitle>Tipo de archivo no soportado </FileTypeErrorModalTitle>
          </FileTypeErrorModalHeader>
        }
        footer={
          <FileTypeErrorModalFooterRoot>
            <AptunoButton onClick={handleCloseTypeFileErrorModal} appearance={VARIANTS.PRIMARY}>
              Aceptar
            </AptunoButton>
          </FileTypeErrorModalFooterRoot>
        }
      >
        El archivo que has intentado subir no tiene un formato permitido. Por favor intenta con un archivo con extensión
        .jpg, .jpeg o .png
      </Modal>
    </InitIdentityValidationScreenRoot>
  );
};
